import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Fuel, Camera, X, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/utils/constants";

export const FuelReportDialog = ({ open, onClose, vehicles, currentUser, onSuccess }) => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [odometer, setOdometer] = useState("");
  const [gallons, setGallons] = useState("");
  const [pumpPhoto, setPumpPhoto] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedVehicle(null);
      setOdometer("");
      setGallons("");
      setPumpPhoto(null);
      setNotes("");
    }
  }, [open]);

  const handleVehicleSelect = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    setSelectedVehicle(vehicle);
    // Pre-fill odometer with current mileage if available
    if (vehicle?.mileage) {
      setOdometer(vehicle.mileage.toLocaleString());
    }
  };

  const handleOdometerChange = (e) => {
    const value = e.target.value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value) || 0;
      setOdometer(numValue > 0 ? numValue.toLocaleString() : '');
    }
  };

  const handleGallonsChange = (e) => {
    const value = e.target.value;
    // Allow decimals for gallons (e.g., 125.5)
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setGallons(value);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    // Compress and convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxWidth = 1200;
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        setPumpPhoto(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!selectedVehicle) {
      toast.error("Please select a truck");
      return;
    }

    const odometerValue = parseInt(odometer.replace(/,/g, ''));
    if (!odometerValue || odometerValue <= 0) {
      toast.error("Please enter a valid odometer reading");
      return;
    }

    const gallonsValue = parseFloat(gallons);
    if (!gallonsValue || gallonsValue <= 0) {
      toast.error("Please enter how many gallons were pumped");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: selectedVehicle.id,
        unit_number: selectedVehicle.unit_number,
        odometer: odometerValue,
        gallons: gallonsValue,
        pump_photo: pumpPhoto,
        notes: notes || null,
        submitted_by: `${currentUser.first_name} ${currentUser.last_name}`,
        submitted_by_id: currentUser.id
      };

      await axios.post(`${API}/fuel-reports`, payload);
      toast.success("Fuel report submitted!");
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Failed to submit fuel report", error);
      toast.error("Failed to submit fuel report");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = selectedVehicle && odometer && gallons;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] m-0 rounded-none flex flex-col fixed inset-0 p-0 overflow-hidden sm:max-w-lg sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:m-auto">
        <DialogHeader className="flex-shrink-0 p-4 border-b bg-blue-50">
          <DialogTitle className="flex items-center gap-2 text-blue-700">
            <Fuel className="w-5 h-5" />
            Fuel Report
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-5" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Select Truck */}
          <div>
            <Label className="text-base font-semibold">Select Truck *</Label>
            <Select value={selectedVehicle?.id || ""} onValueChange={handleVehicleSelect}>
              <SelectTrigger className="h-12 mt-2" data-testid="select-fuel-vehicle">
                <SelectValue placeholder="Choose a truck..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {vehicles.filter(v => v.vehicle_type === 'tractor').map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    Unit {v.unit_number} - {v.year} {v.make} {v.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Odometer Reading */}
          <div>
            <Label className="text-base font-semibold">Odometer Reading *</Label>
            <Input
              value={odometer}
              onChange={handleOdometerChange}
              placeholder="e.g., 150,000"
              className="h-12 mt-2 text-lg"
              inputMode="numeric"
              data-testid="input-fuel-odometer"
            />
            {selectedVehicle?.mileage && (
              <p className="text-xs text-slate-500 mt-1">
                Current recorded: {selectedVehicle.mileage.toLocaleString()} miles
              </p>
            )}
          </div>

          {/* Gallons Pumped */}
          <div>
            <Label className="text-base font-semibold">Gallons Pumped *</Label>
            <Input
              value={gallons}
              onChange={handleGallonsChange}
              placeholder="e.g., 125.5"
              className="h-12 mt-2 text-lg"
              inputMode="decimal"
              data-testid="input-fuel-gallons"
            />
          </div>

          {/* Pump Photo */}
          <div>
            <Label className="text-base font-semibold">Photo of Pump</Label>
            <p className="text-sm text-slate-500 mt-1 mb-3">Take a picture of the pump display</p>

            {pumpPhoto ? (
              <div className="relative rounded-lg overflow-hidden border-2 border-slate-200">
                <img src={pumpPhoto} alt="Pump" className="w-full h-48 object-cover" />
                <button
                  type="button"
                  onClick={() => setPumpPhoto(null)}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <Camera className="w-10 h-10 text-slate-400 mb-2" />
                <span className="text-sm text-slate-600 font-medium">Tap to take photo</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="input-fuel-photo"
                />
              </label>
            )}
          </div>

          {/* Notes (Optional) */}
          <div>
            <Label className="text-base font-semibold">Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
              className="min-h-[80px] mt-2"
              data-testid="input-fuel-notes"
            />
          </div>

          {/* User stamp preview */}
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
            <p><span className="font-medium">Submitted by:</span> {currentUser?.first_name} {currentUser?.last_name}</p>
            <p><span className="font-medium">Date:</span> {new Date().toLocaleString()}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t bg-white flex gap-3" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <Button variant="outline" onClick={onClose} className="flex-1 h-12">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="flex-1 h-12 bg-blue-600 hover:bg-blue-700"
            data-testid="btn-submit-fuel-report"
          >
            {submitting ? "Submitting..." : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Submit Report
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
